import { ChangeEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { Field, FieldInputProps } from 'react-final-form'
import MuiCheckbox, { CheckboxProps } from '@mui/material/Checkbox'
import cn from 'classnames'
import { handleStopPropagation } from '../../../helpers/helpers'
import Icon, { IconName } from '../Icon'
import { TableCellContext } from '../TableCell/TableCell'
import styles from './Checkbox.module.scss'

interface IProps extends CheckboxProps {
  checked?: boolean
  disabled?: boolean
  input?: FieldInputProps<boolean, HTMLElement>
  label?: string
  reactFinalForm?: boolean
  inTable?: boolean
}

// MUI clones its icons with a `fontSize` prop, which this swallows rather than forward to a span.
const CheckboxGlyph = ({ name }: { name: IconName; fontSize?: string }) => (
  <span className={styles.glyph}>
    <Icon name={name} size="sm" />
  </span>
)

const SyntheticCheckbox = ({ isChecked }: { isChecked: boolean }) => {
  return (
    <div className={cn(styles.root, styles.synthetic)}>
      <CheckboxGlyph name={isChecked ? 'checkbox-checked' : 'checkbox-unchecked'} />
    </div>
  )
}

const Checkbox = ({
  input,
  onChange,
  name,
  reactFinalForm = false,
  inTable = false,
  inputProps,
  slotProps,
  ...props
}: IProps) => {
  const context = useContext(TableCellContext)
  const isActive = useMemo(() => context?.isActive, [context])

  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * The input slot, with the caller's own input props merged in rather than
   * clobbered.
   *
   * `inputRef` drives the in-table click-to-activate below, so this component has
   * to own the slot — but MUI resolves the input as `slotProps.input ?? inputProps`,
   * so owning it unconditionally dropped every caller's `aria-label` on the floor
   * and left their checkbox with no accessible name.
   */
  const mergedSlotProps = useMemo(
    () => ({
      ...slotProps,
      input: { ref: inputRef, ...inputProps, ...slotProps?.input },
    }),
    [inputProps, slotProps],
  )
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      input?.onChange(event)
      input?.onBlur()

      if (onChange) {
        onChange(event, checked)
      }
    },
    [input, onChange],
  )

  useEffect(() => {
    if (isActive && inTable && inputRef.current) {
      inputRef.current.click()
    }
  }, [isActive, inTable, inputRef])
  if (reactFinalForm && name) {
    return (
      <Field<boolean>
        name={name}
        type="checkbox"
        subscription={{ value: true }}
        render={({ input: rffInput }) => {
          const currentChecked = Boolean(rffInput.value)
          if (inTable && !isActive) {
            return <SyntheticCheckbox isChecked={currentChecked} />
          }
          return (
            <MuiCheckbox
              {...props}
              name={name}
              checked={currentChecked}
              classes={{ root: styles.root }}
              color="primary"
              onChange={(event, checked) => {
                rffInput.onChange(event)
                rffInput.onBlur()
                if (onChange) {
                  onChange(event, checked)
                }
              }}
              icon={
                <CheckboxGlyph
                  name={props.disabled ? 'checkbox-unchecked-disabled' : 'checkbox-unchecked'}
                />
              }
              checkedIcon={
                <CheckboxGlyph
                  name={props.disabled ? 'checkbox-checked-disabled' : 'checkbox-checked'}
                />
              }
              onClick={handleStopPropagation}
              slotProps={mergedSlotProps}
            />
          )
        }}
      />
    )
  }

  return (
    <MuiCheckbox
      {...props}
      checked={input?.checked ?? props.checked ?? false}
      classes={{ root: styles.root }}
      color="primary"
      onChange={handleChange}
      icon={
        <CheckboxGlyph
          name={props.disabled ? 'checkbox-unchecked-disabled' : 'checkbox-unchecked'}
        />
      }
      checkedIcon={
        <CheckboxGlyph name={props.disabled ? 'checkbox-checked-disabled' : 'checkbox-checked'} />
      }
      onClick={handleStopPropagation}
      slotProps={mergedSlotProps}
    />
  )
}

export default Checkbox
